import { Course, PredefinedOnlineSelections, PredefinedOnlineSelectionsWithCourses } from '@lx/shared/data-access/types';

/**
 * Merges predefined online selections with course data.
 *
 * This utility function combines full course details with predefined selection metadata
 * (duration, start date, weight, etc.) to create a complete data structure ready for display.
 *
 * **Merge Strategy:**
 * 1. For each predefined selection, find the matching course by courseId
 * 2. Spread course properties first, then override with selection properties
 * 3. Sort by weight (ascending) to maintain display order
 *
 * **Why a utility function:**
 * - Pure function - no side effects, easy to test
 * - Reusable across services and components
 * - Single source of truth for merge logic
 * - Can be used in computed signals or observables
 *
 * @param predefinedOnlineSelections - Predefined course packages with durations and dates
 * @param courses - Available courses with full course details
 * @returns Merged course options sorted by weight
 *
 * @example
 * // In a computed signal:
 * protected readonly courseOptions = computed(() =>
 *   mergePredefinedOnlineSelectionsWithCourses(
 *     this.predefinedSelections(),
 *     this.availableCourses()
 *   )
 * );
 *
 * @example
 * // In an observable pipe:
 * combineLatest([courses$, selections$]).pipe(
 *   map(([courses, selections]) =>
 *     mergePredefinedOnlineSelectionsWithCourses(selections, courses)
 *   )
 * );
 */
export function mergePredefinedOnlineSelectionsWithCourses(
  predefinedOnlineSelections: PredefinedOnlineSelections[],
  courses: Course[],
): PredefinedOnlineSelectionsWithCourses[] {
  if (!predefinedOnlineSelections || !courses) {
    return [];
  }

  return predefinedOnlineSelections
    .map((predefinedOnlineSelection) => ({
      ...courses.find(({ code }) => code === predefinedOnlineSelection?.courseId),
      ...predefinedOnlineSelection,
    }))
    .sort((a, b) => (a?.weight ?? Number.MAX_SAFE_INTEGER) - (b?.weight ?? Number.MAX_SAFE_INTEGER));
}
